import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import Receipt from "../components/Receipt";
import { canShowReceipt, customerStatusLabel } from "../utils/orderHelpers";

async function readError(fnError) {
  const fallback = "We couldn't look up that order. Please try again.";
  try {
    const body = await fnError?.context?.json?.();
    return body?.error || fallback;
  } catch {
    return fallback;
  }
}

// For guests (and anyone not signed in): the order number AND the email used at checkout
// must both match, so nobody can browse other people's orders.
export default function OrderLookup() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError("");
    setOrder(null);

    const { data, error: fnError } = await supabase.functions.invoke("get-receipt", {
      body: {
        orderId: orderId.trim().replace(/^#/, ""),
        email: email.trim(),
      },
    });

    if (fnError) {
      setError(await readError(fnError));
    } else if (!data || data.error) {
      setError(data?.error || "We couldn't look up that order. Please try again.");
    } else {
      setOrder(data);
    }

    setLoading(false);
  }

  return (
    <>
      <div className="ord" style={order ? { paddingBottom: 0 } : undefined}>
        <div className="no-print">
          <h2 className="ord-title">Find your order</h2>
          <p className="ord-sub">
            Enter your order number and the email address you used at checkout. You'll find both
            in your confirmation email.
          </p>

          <form className="lookup-card" onSubmit={handleSubmit} noValidate>
            {error && <p className="ui-alert ui-alert--error" role="alert">{error}</p>}

            <div className="ui-field">
              <label className="ui-label" htmlFor="lookup-order">Order number</label>
              <input
                id="lookup-order"
                className="ui-input"
                inputMode="numeric"
                placeholder="e.g. 164"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
              />
            </div>

            <div className="ui-field">
              <label className="ui-label" htmlFor="lookup-email">Email address</label>
              <input
                id="lookup-email"
                type="email"
                className="ui-input"
                placeholder="you@example.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <button
              type="submit"
              className="ui-btn ui-btn--primary ui-btn--block"
              disabled={!orderId.trim() || !email.trim() || loading}
            >
              {loading ? "Searching..." : "Find order"}
            </button>
          </form>

          <p className="ord-note">
            Have an account? <Link to="/account/orders">Sign in to see all your orders</Link>.
          </p>
        </div>

        {order && !canShowReceipt(order) && (
          <p className="ui-alert ui-alert--ok no-print" style={{ marginTop: "1.5rem" }}>
            Order #{order.id} is currently "{customerStatusLabel(order.status)}". A receipt will
            be available once payment has been confirmed.
          </p>
        )}
      </div>

      {order && canShowReceipt(order) && <Receipt order={order} />}
    </>
  );
}